import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TicketEditorDrawer from './components/TicketEditorDrawer';
import { useAuth } from '../../context/AuthProvider';
import type { AppDispatch, RootState } from '../../store';
import { fetchProjects, selectAllProjects } from '../../store/features/projects/projectsSlice';
import {
  assignTicket,
  fetchTicketDetail,
  selectCachedTicketById,
  ticketRemoved,
  ticketUpserted,
  updateTicketStatus,
} from '../../store/features/tickets/ticketsSlice';
import { buildTicketEditorTicket } from '../../lib/tickets/ticketEditorTicket';
import type { TicketStatus } from '../../store/features/tickets/types';
import { apiFetch } from '../../lib/api';
import { getUserTimezone } from '../../utils/timezone';
import {
  closeTicketPortal,
  selectOpenTicketPortalId,
} from '../../store/features/ticketPortal/ticketPortalSlice';

type UiPriority = 'low' | 'medium' | 'high' | 'urgent';

type TicketPortalPatch = Partial<{
  title: string;
  description: string;
  projectId: string;
  milestoneId: string | null;
  priority: UiPriority;
  startDate: string;
  dueDate: string;
  assignedAgentId: string | null;
  assignedOrgMemberId: string | null;
  owningTeamNodeId: string | null;
  clientSubscriptionId: string | null;
  offeringId: string | null;
  offeringSubcategoryId: string | null;
  kbaseProcessId: string | null;
  estimatedMinutes: number | null;
  blockedByTicketIds: string[];
  handoffToTicketIds: string[];
  status: TicketStatus;
  subtasks: any[];
  participants: string[];
}>;

function mapPriorityToApi(priority: UiPriority) {
  if (priority === 'urgent') return 'P0';
  if (priority === 'high') return 'P1';
  if (priority === 'low') return 'P3';
  return 'P2';
}

export default function StaffTicketPortalHost() {
  const { role } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const ticketId = useSelector(selectOpenTicketPortalId);
  const projects = useSelector(selectAllProjects);
  const ticket = useSelector((state: RootState) =>
    ticketId ? selectCachedTicketById(ticketId)(state) : undefined,
  );
  const viewerTimezone = React.useMemo(() => getUserTimezone() || 'UTC', []);

  const isStaff = role === 'admin' || role === 'agent';

  React.useEffect(() => {
    if (!isStaff || !ticketId) return;
    void dispatch(fetchTicketDetail({ id: ticketId }));
    if (!projects.length) {
      void dispatch(fetchProjects());
    }
  }, [dispatch, isStaff, projects.length, ticketId]);

  const editorTicket = React.useMemo(() => buildTicketEditorTicket(ticket), [ticket]);

  const project = React.useMemo(() => {
    if (!editorTicket?.projectId) return undefined;
    return projects.find((entry) => entry.id === editorTicket.projectId) || undefined;
  }, [editorTicket?.projectId, projects]);

  const handleClose = React.useCallback(() => {
    dispatch(closeTicketPortal());
  }, [dispatch]);

  const handleDelete = React.useCallback(
    async (id: string) => {
      await apiFetch(`/api/tickets/${id}`, { method: 'DELETE' });
      dispatch(ticketRemoved(id));
      dispatch(closeTicketPortal());
    },
    [dispatch],
  );

  const hydratedTicket = React.useMemo(() => {
    if (!editorTicket) return null;
    return {
      ...editorTicket,
      onDelete: handleDelete,
    };
  }, [editorTicket, handleDelete]);

  const handleUpdate = React.useCallback(
    async (id: string, patch: TicketPortalPatch) => {
      const body: Record<string, any> = {};

      if (patch.title !== undefined) body.title = patch.title;
      if (patch.description !== undefined) body.description = patch.description;
      if (patch.projectId !== undefined) body.projectId = patch.projectId || null;
      if (patch.milestoneId !== undefined) body.milestoneId = patch.milestoneId || null;
      if (patch.priority !== undefined) body.priority = mapPriorityToApi(patch.priority);
      if (patch.assignedOrgMemberId !== undefined) {
        body.assignedOrgMemberId = patch.assignedOrgMemberId || null;
      }
      if (patch.owningTeamNodeId !== undefined) {
        body.owningTeamNodeId = patch.owningTeamNodeId || null;
      }
      if (patch.clientSubscriptionId !== undefined) {
        body.clientSubscriptionId = patch.clientSubscriptionId || null;
      }
      if (patch.offeringId !== undefined) body.offeringId = patch.offeringId || null;
      if (patch.offeringSubcategoryId !== undefined) {
        body.offeringSubcategoryId = patch.offeringSubcategoryId || null;
      }
      if (patch.kbaseProcessId !== undefined) {
        body.kbaseProcessId = patch.kbaseProcessId || null;
      }
      if (patch.estimatedMinutes !== undefined) {
        body.estimatedMinutes = patch.estimatedMinutes;
        body.remainingEstimateMinutes = patch.estimatedMinutes;
      }
      if (patch.blockedByTicketIds !== undefined) {
        body.blockedByTicketIds = patch.blockedByTicketIds;
      }
      if (patch.handoffToTicketIds !== undefined) {
        body.handoffToTicketIds = patch.handoffToTicketIds;
      }
      if (patch.startDate !== undefined) {
        body.startDate = patch.startDate || null;
        body.timezone = viewerTimezone;
      }
      if (patch.dueDate !== undefined) {
        body.dueDate = patch.dueDate || null;
        body.timezone = viewerTimezone;
      }
      if (patch.subtasks !== undefined) body.subtasks = patch.subtasks;
      if (patch.participants !== undefined) body.participants = patch.participants;

      if (Object.keys(body).length) {
        const response = await apiFetch<any>(`/api/tickets/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (response?.id) {
          dispatch(ticketUpserted(response));
        }
      }

      if (
        patch.assignedAgentId !== undefined &&
        patch.assignedAgentId !== (ticket?.assignedAgentId ?? null)
      ) {
        await dispatch(assignTicket({ id, agentId: patch.assignedAgentId })).unwrap();
      }

      if (patch.status && patch.status !== ticket?.status) {
        await dispatch(updateTicketStatus({ id, status: patch.status })).unwrap();
      }
    },
    [dispatch, ticket?.assignedAgentId, ticket?.status, viewerTimezone],
  );

  if (!isStaff) return null;

  return (
    <TicketEditorDrawer
      open={Boolean(ticketId && hydratedTicket)}
      mode="edit"
      onClose={handleClose}
      project={project}
      projects={projects as any}
      ticket={hydratedTicket}
      agents={[]}
      onCreate={async () => ''}
      onUpdate={handleUpdate}
    />
  );
}
